import { useEffect, useState } from 'react';
import { UseFormReturn, get, useFormState } from 'react-hook-form';
import { Nullable, TextInputField } from '@brighte/form-components';
import { IncomeVerificationType, requiredFieldError } from '@brighte/validation';
import { BusinessSearch, useBusinessSearch } from '@hooks';
import { Columns } from '@spark-web/columns';
import { Combobox } from '@spark-web/combobox';
import { Field } from '@spark-web/field';
import { SearchIcon } from '@spark-web/icon';
import { InputAdornment } from '@spark-web/text-input';
import { LABELS } from '@utils';

type ABNInputProps = Pick<UseFormReturn<IncomeVerificationType>, 'control' | 'setValue' | 'register'>;
const ABN_FIELD_PATH = 'applicant.employment.selfEmployed.abn';
const ENTITY_NAME_FIELD_PATH = 'applicant.employment.selfEmployed.entityName';

function ABNInput({ control, setValue, register }: ABNInputProps) {
  const { errors, defaultValues } = useFormState({ control });
  const abnNumber = defaultValues?.applicant?.employment?.selfEmployed?.abn;
  const fieldError = get(errors, ABN_FIELD_PATH)?.message;

  const [showManual, setShowManual] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const handleInputChange = (value: string) => {
    setInputValue(value);
  };
  const { data: options, isFetching } = useBusinessSearch(inputValue);

  const items: BusinessSearch[] = options?.map(option => option) ?? [];

  const handleChange = (newValue: Nullable<BusinessSearch>) => {
    if (newValue?.abn === LABELS.ENTER_MANUALLY) {
      setShowManual(true);
      setValue(ABN_FIELD_PATH, '');
      setValue(ENTITY_NAME_FIELD_PATH, '');
    } else {
      setShowManual(false);
      setValue(ABN_FIELD_PATH, newValue?.abn ?? '');
      setValue(ENTITY_NAME_FIELD_PATH, newValue?.name ?? '');
    }
  };

  useEffect(() => {
    if (abnNumber) {
      setInputValue(abnNumber);
      setValue(ABN_FIELD_PATH, abnNumber);
    }
  }, []);

  return (
    <>
      {/*
        Keep abn/entityName registered for the lifetime of ABNInput so the values persist when
        sibling components (e.g. SelfEmployed inside ManualIncomeFields) unmount on the CDR /
        Fiskil path. shouldUnregister:false overrides the form-level shouldUnregister:true so
        the values aren't wiped between gate transitions.
      */}
      <input type="hidden" {...register(ABN_FIELD_PATH, { shouldUnregister: false })} />
      <input type="hidden" {...register(ENTITY_NAME_FIELD_PATH, { shouldUnregister: false })} />
      <Field
        label={LABELS.ABN_TRADING_NAME}
        tone="critical"
        message={fieldError && !showManual && requiredFieldError()}
        disabled={showManual}
      >
        <Combobox
          placeholder="Find your business name..."
          items={items}
          isLoading={isFetching}
          onChange={handleChange}
          inputValue={inputValue || ''}
          onInputChange={handleInputChange}
          getOptionLabel={(option: BusinessSearch) =>
            option.name ? `${option.abn} / ${option.name}` : `${option.abn}`
          }
          getOptionValue={(option: BusinessSearch) => option.abn}
          defaultOption={
            inputValue && inputValue.trim().length > 3
              ? {
                  option: { abn: LABELS.ENTER_MANUALLY, name: '' },
                }
              : undefined
          }
          startAdornment={
            <InputAdornment placement="start">
              <SearchIcon size="xxsmall" />
            </InputAdornment>
          }
        />
      </Field>
      {showManual ? (
        <Columns gap="large" collapseBelow="tablet">
          <TextInputField name="applicant.employment.selfEmployed.abn" label={LABELS.ABN_NUMBER} control={control} />
          <TextInputField
            name="applicant.employment.selfEmployed.entityName"
            label={LABELS.TRADING_NAME}
            control={control}
          />
        </Columns>
      ) : null}
    </>
  );
}

export default ABNInput;
